import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { FilterChips } from "../../../common/u1/FilterChips";
import { Pagination } from "../../../common/u1/Pagination";
import { Button } from "../../../common/u1/Button";
import { SearchField } from "../../../common/u1/SearchField";
import { nameMatches } from "../../../common/u1/filters";
import { DocumentIcon } from "../../../../assets/ui/icons";
import { useGetUserPurchaedDataQuery } from "../../../../store/Company/companyApiSlice";
import { PurchasedCompanyRow } from "./PurchasedCompanyRow";
import { CompanyNameProbe } from "./CompanyNameProbe";
import { purchaseKey } from "./usePurchasedCompany";
import {
  mergePurchases,
  reportStatus,
  purchaseState,
  matchesFilter,
  PURCHASE_FILTERS,
} from "./purchasedState";

/* The businesses you have bought a profile for. */
export const PAGE_SIZE = 5;

export const PurchasedList = () => {
  const { userInfo, token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  /* Business names, learned from the probes below. Keyed by the same id the
     rows are keyed by. */
  const [names, setNames] = useState({});

  /* Stable, and a no-op when the name has not changed — a probe re-reporting
     the same name must not start a render loop. */
  const resolveName = useCallback((id, name) => {
    setNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
  }, []);

  /* Guarded: the live screen reads `userInfo?.individual[0]`, which throws for
     a user with no individual record. The loaded case is identical. */
  const { data } = useGetUserPurchaedDataQuery({
    token,
    id: userInfo?.individual?.[0]?.userID,
  });

  const purchases = useMemo(() => mergePurchases(data), [data]);

  /* Each row's state is decided once, here, so the chips, the count and the
     rows cannot disagree about what a purchase is. */
  const withState = useMemo(
    () =>
      purchases.map((row, i) => {
        /* A TREVEX purchase is matched against `worldboxorder`, which is the
           only array carrying a `companyID`; an order-backed row reports its
           own status. See the notes on worldboxStatus and orderStatus. */
        const status = reportStatus(row, data?.worldboxorder);
        return { id: purchaseKey(row, i), row, status, state: purchaseState(row, status) };
      }),
    [purchases, data?.worldboxorder]
  );

  /* The name a row can be searched by: the one its lookup resolved, or the
     one the order row already carried. An order-backed purchase is therefore
     findable before its probe has answered. */
  const searchName = useCallback(
    (item) => names[item.id] || item.row?.company_name || "",
    [names]
  );

  const filtered = useMemo(
    () =>
      withState.filter(
        (item) => matchesFilter(item.state, filter) && nameMatches(searchName(item), query)
      ),
    [withState, filter, query, searchName]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  /* Clamped during render: changing the filter can leave the current page past
     the end, and correcting it in an effect would paint an empty list first. */
  const current = Math.min(page, totalPages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const changeFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  /* Both narrowing controls reset the page: narrowing from page 3 would
     otherwise land on an empty one. */
  const search = (value) => {
    setQuery(value);
    setPage(1);
  };

  const startPurchasing = () => {
    window.scrollTo(0, 0);
    navigate("/plans");
  };

  return (
    <section className="min-w-0">
      {/* Resolves every purchase's name so the search field can see all of
          them, not just the page on screen. Renders nothing. */}
      {withState.map((item) => (
        <CompanyNameProbe
          key={`probe-${item.id}`}
          id={item.id}
          row={item.row}
          token={token}
          onResolve={resolveName}
        />
      ))}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="m-0 text-[26px] font-bold tracking-[-.01em] text-[var(--ink)] font-sans">
            Acquired business profiles
          </h2>
          <p className="mt-1.5 mb-0 max-w-[520px] text-[14.5px] leading-[1.5] text-[var(--ink-2)] font-sans">
            Every Web Profile you have ordered. Claimed and Verified businesses open for review
            straight away. Everything else becomes a report our analysts build for you.
          </p>
        </div>

        {/* Narrows the purchases on screen — it does not search the directory.
            Hidden with nothing bought, where there is nothing to narrow. */}
        {withState.length > 0 && (
          <SearchField
            value={query}
            onChange={search}
            placeholder="Search business"
            label="Search your purchased businesses"
            className="shrink-0 sm:w-[320px]"
          />
        )}
      </div>

      {/* Transcribed empty branch: nothing bought at all. */}
      {withState.length === 0 ? (
        <div className="mt-6 rounded-[18px] border border-[var(--line)] bg-white p-10 text-center shadow-[0_1px_2px_rgba(20,40,60,.03)]">
          <span
            aria-hidden="true"
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--coral-soft)] text-[var(--coral)]"
          >
            <DocumentIcon size={26} />
          </span>
          <h3 className="m-0 text-[17px] font-bold text-[var(--ink)] font-sans">
            No purchases yet
          </h3>
          <p className="mx-auto mt-1.5 mb-5 max-w-[360px] text-[13.5px] leading-[1.5] text-[var(--muted)] font-sans">
            All your purchases will appear here once you order a Web Profile.
          </p>
          <Button variant="coral" onClick={startPurchasing}>
            Start purchasing
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">
              {withState.length} {withState.length === 1 ? "purchase" : "purchases"}
            </p>
            <FilterChips
              value={filter}
              onChange={changeFilter}
              filters={PURCHASE_FILTERS}
              label="Filter purchases by state"
            />
          </div>

          {filtered.length === 0 ? (
            /* A search that matches nothing is not the same as a state with
               nothing in it. */
            <p className="mt-6 mb-0 rounded-xl border border-[var(--line)] bg-white px-5 py-8 text-center text-[14px] text-[var(--muted)] font-sans">
              {query.trim()
                ? "No purchases match that search."
                : "No purchases in this state."}
            </p>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {visible.map((item) => (
                <PurchasedCompanyRow
                  key={item.id}
                  row={item.row}
                  status={item.status}
                  token={token}
                />
              ))}
            </div>
          )}

          <Pagination
            page={current}
            totalPages={totalPages}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            itemLabel="purchase"
            itemLabelPlural="purchases"
            onChange={setPage}
            label="Purchased pages"
          />
        </>
      )}
    </section>
  );
};

export default PurchasedList;
